import React, {
  FunctionComponent,
  Suspense,
  lazy,
  useContext,
  useEffect
} from 'react';
import { UserAgentContext } from 'features/user-agent/userAgent';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.scss';
import { AuthModal } from 'features/auth/AuthModal';
import ReactModal from 'react-modal';
import { Loader } from 'components/Loader';
// Lazy so the network view (and its ChannelNetworkModal/List, ~40 KB) stays out
// of the entry chunk. The router lazy-imports the same component for /network;
// importing it statically here would defeat that split (Rolldown INEFFECTIVE_DYNAMIC_IMPORT).
const ChannelNetwork = lazy(() =>
  import('features/channel/components/ChannelNetwork').then((m) => ({
    default: m.ChannelNetwork
  }))
);
const NetworkHome = lazy(() =>
  import('features/network/NetworkHome').then((m) => ({
    default: m.NetworkHome
  }))
);
import { Alert } from 'features/alert/alert';
import { Privacy } from 'features/privacy/privacy';
import { ChannelSubscription } from 'features/channel_subscription/channelSubscription';
import { EventMiniPlayer } from 'features/events/components/EventMiniPlayer';
import { RecordingMiniPlayer } from 'features/recordings/components/RecordingMiniPlayer';
import { Search } from 'features/search/Search';
import { StateTester } from 'features/statetester/StateTester';
import { useNativeEvents } from 'hooks/useNativeEvents';
import { useAudio } from 'features/audio/audio';
import { useDispatch } from 'react-redux';
import { WebSocketContext } from 'features/hub/hub';
import { useOnceEffect } from 'hooks/useOnceEffect';
import { setCurrentVolume } from 'features/audio/audioSlice';
import { SendRecordingListenEvents } from 'features/recordings/components/SendRecordingListenEvents';
import { HelmetTags } from 'components/Helmet';
import './tailwind.css';
import { LiveBanner } from 'features/events/components/LiveBanner';
import { useBypassAccessCodeNetwork } from 'features/channel/channelHooks';
import { AnimatedRouteSwitch } from 'AnimatedRouteSwitch';
import { useAppSelector } from 'hooks/redux';

const App: FunctionComponent = () => {
  const {
    isNativeAppWebview,
    isDACreatorApp,
    isListenerAppWebview,
    isiOSListenerAppVersion
  } = useContext(UserAgentContext);
  const {
    postMessage,
    dynamicSizeUpdatesEvent,
    qWebChannel,
    eventWindowEvents
  } = useNativeEvents();
  const { connectToSocket, leaveCrowd } = useContext(WebSocketContext);
  const { setVolume, volume, stop } = useAudio();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const location = useLocation() as any;
  const bypassAccessCodeNetwork = useBypassAccessCodeNetwork();

  const channelHomepageEnabled = useAppSelector(
    (state) => state.channel.channelDetails?.channelHomepageEnabled
  );
  const isNetwork = useAppSelector((state) => !!state.network.networkDetails);

  const { requiresAccesscode, requiresChannelSubscription } = useAppSelector(
    (state) => {
      return {
        requiresAccesscode: state.channel.requiresAccesscode,
        requiresChannelSubscription: state.channel.requiresChannelSubscription
      };
    }
  );

  useEffect(
    function stopAudioFromEventErrorRedirect() {
      if (location.state && location.state.stopAudio) {
        stop(true);
      }
    },
    [location, stop]
  );

  useEffect(() => {
    if (isNativeAppWebview) {
      postMessage('javascriptLoaded');
      dynamicSizeUpdatesEvent();
    }
  }, [postMessage, dynamicSizeUpdatesEvent, isNativeAppWebview]);

  useEffect(() => {
    connectToSocket();
  }, [connectToSocket]);

  useEffect(() => {
    if (isDACreatorApp) {
      qWebChannel();
    }
  }, [isDACreatorApp, qWebChannel]);

  useEffect(() => {
    if (isNativeAppWebview) {
      eventWindowEvents(pathname, navigate);
    }
  }, [isNativeAppWebview, eventWindowEvents, pathname, navigate]);

  useEffect(() => {
    if (isListenerAppWebview && channelHomepageEnabled) {
      postMessage({
        requiresAccesscode: requiresAccesscode
      });
    }
  }, [
    requiresAccesscode,
    isListenerAppWebview,
    postMessage,
    channelHomepageEnabled
  ]);

  useEffect(() => {
    if (isDACreatorApp) {
      qWebChannel();
    }
  }, [isDACreatorApp, qWebChannel]);

  useOnceEffect(() => {
    dispatch(setCurrentVolume(volume));
  });

  useEffect(
    function hideTabsOnListenerAppChannelDisabled() {
      if (isiOSListenerAppVersion('4.1.4')) {
        postMessage({
          channelHomepageEnabled: channelHomepageEnabled
        });
      } else if (isListenerAppWebview && !channelHomepageEnabled) {
        postMessage({
          requiresAccesscode: true
        });
      }
    },
    [
      channelHomepageEnabled,
      isListenerAppWebview,
      isiOSListenerAppVersion,
      postMessage
    ]
  );

  useEffect(() => {
    const handleUnload = () => {
      if (volume === 0) {
        setVolume(1);
      }
      leaveCrowd();
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
    };
  }, [volume, setVolume, leaveCrowd]);

  if (isNetwork) {
    return (
      <>
        <HelmetTags />
        <Suspense fallback={<Loader fillComponent={true} />}>
          <NetworkHome />
        </Suspense>
      </>
    );
  }

  if (bypassAccessCodeNetwork) {
    return (
      <>
        <HelmetTags />
        <Suspense fallback={<Loader fillComponent={true} />}>
          <ChannelNetwork />
        </Suspense>
      </>
    );
  }

  if (requiresAccesscode) {
    return (
      <>
        <HelmetTags />
        <Privacy />
      </>
    );
  }

  if (requiresChannelSubscription) {
    return (
      <>
        <HelmetTags />
        <ChannelSubscription />
        <AuthModal />
      </>
    );
  }

  return (
    <>
      <HelmetTags />
      <Alert />
      <AnimatedRouteSwitch />
      <AuthModal />
      <LiveBanner />
      <EventMiniPlayer />
      <RecordingMiniPlayer />
      <SendRecordingListenEvents />
      <Search />
      {(import.meta.env.VITE_ENV_NAME === 'development' ||
        import.meta.env.VITE_ENV_NAME === 'staging') && <StateTester />}
    </>
  );
};

ReactModal.setAppElement('#root');

export default App;
